/**
 * SkillEmblem — the living procedural card emblem (BL-57, ART §9 + §6.8).
 *
 * The S88 cards stamped a flat clip-art SVG glyph (`CATEGORY_ICONS`) onto a dark
 * rectangle — the owner's CR: *"zu flach, die Symbole zu einfach + statisch"*.
 * This replaces that glyph with a **procedural canvas emblem** built from the
 * engine's OWN material: an extruded/bevelled silhouette lit by the real
 * `emissiveFill` (the §6.1 off-centre hot-core→mid→rim falloff that also reads as
 * fake-normal volume), a bright rim, additive `glowCache`-style vertex seeds, and
 * — when present — the shared `UIBloom` flaring the hot edges. The emblem is the
 * card's hero: dimensional, archetype-shaped (offensive = aggressive/edged,
 * defensive = shielded/solid, utility = orbital/precise), escalating in detail
 * and bloom by rarity.
 *
 * Motion (owner CR 2026-06-05: *"sie sollten sich immer bewegen … abgeschwächt
 * wenn die Maus nicht drauf ist"*): the emblem is **always alive** — a gentle,
 * ~30fps-throttled idle (a slow ± rock + slow orbit drift + a faint breathe)
 * runs while at rest, ramping up to the confident **hover** spin/power-up on
 * cursor-over and the select flare. It is the card's hero, so it animates; the
 * draft is a between-wave **modal** (the arena isn't drawing), so the GPU budget
 * is free here. No per-card WebGL context — the bloom routes through the ONE
 * shared `UIBloom` the host passes in (the S88 `getCardBloom()`); the per-frame,
 * GPU-glowing apex is the separate `LegendaryEmblem` (WebGL).
 *
 * Motion is `isReducedMotion()`-gated everywhere: reduced → a single static draw
 * (rim-lit, bloomed, dimensional — just no spin / construct / parallax).
 */
import { emissiveFill, deriveRamp } from "../rendering/EmissiveMaterial.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { isReducedMotion } from "../engine/MotionSettings.ts";
import { UIBloom } from "./UIBloom.ts";

// ─── Shared WebGL bloom (one context for all emblems; the arena GLSL, reused) ──
//
// BL-60: the shared card-bloom moved here (it was `SkillCard.getCardBloom`) so a
// skill→`TradingCard` adapter and the emblem share ONE feature-detected context.
// Null in headless / no-WebGL2 → tests + the BL-44 degrade rung never touch GL.
let sharedBloom: UIBloom | null | undefined;

/** Max drawn radius (px) of the emblem hero — keeps it a contained showcase that
 *  never frame-fills or overlaps the card title, however tall the art window is.
 *  BL-60 polish (owner CR 2026-06-06): the card is **text-first** — the emblem is a
 *  *small* hero, the effect text is the point — so the cap is distinctly lower. */
const MAX_EMBLEM_R = 36;

/** The drawable archetypes — `legendary` falls through to a rich gold sigil used
 *  only by the enriched-canvas fallback rung (D′); the live legendary is WebGL. */
export type EmblemArchetype =
  | "offensive"
  | "rate"
  | "range"
  | "chill"
  | "pierce"
  | "economy"
  | "synergy"
  | "tradeoff"
  | "instant"
  | "enemies"
  | "defensive"
  | "utility"
  | "legendary";

/** Map a card category to an emblem archetype (utility = the safe default).
 *  The COARSE fallback — `emblemArchetypeForCard` is what the surfaces use. */
export function emblemArchetype(category: string): EmblemArchetype {
  if (category === "offensive") return "offensive";
  if (category === "defensive") return "defensive";
  if (category === "legendary") return "legendary";
  return "utility";
}

/** BL-298 — the SEMANTIC archetype, read off the card's own shipped `effect`.
 *  `effect.type` is the sharp axis (37 distinct values over 45 cards); where a
 *  type has no dedicated silhouette the broader `effect.target` decides, and the
 *  category is the last resort. Legendary keeps its star: the chase card's
 *  silhouette IS its rarity, which is the first thing the player reads. */
const TYPE_ARCHETYPE: Record<string, EmblemArchetype> = {
  globalFireRateMultiplier: "rate",
  triComboAutoFire: "rate",
  globalRangeMultiplier: "range",
  revealWaves: "range",
  showAllHp: "range",
  intrusionScanner: "range",
  globalSlow: "chill",
  firewallSlow: "chill",
  timeWarp: "chill",
  piercingShots: "pierce",
  targetFastest: "pierce",
  apexPredator: "pierce",
  bossDamageMultiplier: "pierce",
  killGoldMultiplier: "economy",
  addCredits: "economy",
  sellMultiplier: "economy",
  greedProtocol: "economy",
  bountyProtocol: "economy",
  diagonalSynergy: "synergy",
  synergyFrequency: "synergy",
  resonance: "synergy",
  cascade: "synergy",
  addLives: "defensive",
  reduceMaxHp: "enemies",
  scorchedEarth: "enemies",
};
const TARGET_ARCHETYPE: Record<string, EmblemArchetype> = {
  combat: "offensive",
  instant: "instant",
  synergy: "synergy",
  economy: "economy",
  tradeoff: "tradeoff",
  enemies: "enemies",
  player: "defensive",
  firewall: "defensive",
  ui: "range",
  meta: "utility",
};

export function emblemArchetypeForCard(card: {
  category: string;
  rarity?: string;
  effect?: { type?: string; target?: string };
}): EmblemArchetype {
  if (card.rarity === "legendary" || card.category === "legendary") {
    return "legendary";
  }
  const byType = card.effect?.type
    ? TYPE_ARCHETYPE[card.effect.type]
    : undefined;
  if (byType) return byType;
  const byTarget = card.effect?.target
    ? TARGET_ARCHETYPE[card.effect.target]
    : undefined;
  if (byTarget) return byTarget;
  return emblemArchetype(card.category);
}

export interface SkillEmblemOptions {
  archetype: EmblemArchetype;
  /** Identity mid hue (the rarity `mid` — `deriveRamp` gives core/rim). */
  colour: string;
  /** Bloom weight 0..1 — 0 = no bloom seed/flare (common stays flat-lit). */
  bloomWeight: number;
  /** The ONE shared card-bloom (host-owned). Null in headless / no-WebGL. */
  bloom: UIBloom | null;
  /** Rarity intensity multiplier on the lit edge (the §3 escalation). */
  intensity?: number;
  /** BL-296 — override the drawn-radius cap for a HOST that gives the emblem a
   *  genuinely large art window (the draft card). Omitted everywhere else, so
   *  every compact surface keeps the `MAX_EMBLEM_R` read byte-for-byte. */
  maxRadius?: number;
}

/** Overscan (px) so the rim glow / bloom flare bleeds past the emblem box. */
const OVERSCAN = 16;

/** A 2D unit polygon (verts in -1..1) + per-archetype accent geometry. */
interface Shape {
  verts: [number, number][];
  /** Accent kind drawn over the lit face (the archetype's "tell"). */
  accent: "cross" | "ring" | "orbit" | "star";
}

const SQRT3_2 = Math.sqrt(3) / 2;

function regularPoly(n: number, rot = -Math.PI / 2): [number, number][] {
  const v: [number, number][] = [];
  for (let i = 0; i < n; i++) {
    const a = rot + (i / n) * Math.PI * 2;
    v.push([Math.cos(a), Math.sin(a)]);
  }
  return v;
}

function starPoly(points: number, inner: number): [number, number][] {
  const v: [number, number][] = [];
  for (let i = 0; i < points * 2; i++) {
    const a = -Math.PI / 2 + (i / (points * 2)) * Math.PI * 2;
    const r = i % 2 === 0 ? 1 : inner;
    v.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return v;
}

/** A chevron stack — the "faster" silhouette (rate). */
function chevron(): [number, number][] {
  return [
    [0, -1],
    [0.92, 0.05],
    [0.5, 0.05],
    [0, -0.5],
    [-0.5, 0.05],
    [-0.92, 0.05],
    [0, -0.28],
    [0.92, 0.72],
    [0, 1],
    [-0.92, 0.72],
  ];
}

/** A wide flat lens — the "reach" silhouette (range). */
function lens(): [number, number][] {
  return regularPoly(10).map(([x, y]) => [x * 1.18, y * 0.5] as [number, number]);
}

/** A bowtie / hourglass — the "you give something up" silhouette (tradeoff). */
function bowtie(): [number, number][] {
  return [
    [-0.92, -1],
    [0.92, -1],
    [0.16, 0],
    [0.92, 1],
    [-0.92, 1],
    [-0.16, 0],
  ];
}

/** A bolt — the "right now, once" silhouette (instant). */
function bolt(): [number, number][] {
  return [
    [0.16, -1],
    [0.86, -0.16],
    [0.3, -0.1],
    [0.72, 1],
    [-0.2, -0.02],
    [0.3, -0.06],
    [-0.5, -0.62],
  ];
}

/** A narrow kite — the "goes through" silhouette (pierce / targeting). */
function kite(): [number, number][] {
  return [
    [0, -1],
    [0.42, 0.1],
    [0, 1],
    [-0.42, 0.1],
  ];
}

/** BL-298 — the emblem shape library. Before this pass there were FOUR shapes for
 *  45 skills, keyed on the four card categories, so 21 offensive skills all wore
 *  the same triangle: the owner's exact report was that CACHE_HIT and
 *  ÜBERLAST::EMP are indistinguishable before you read the name. Each shape below
 *  answers a different question the player is actually asking — does this hit
 *  harder, more often, further; does it slow, pierce, pay, trade, fire once —
 *  and is derived from the SHIPPED `effect` fields, never invented per card. */
const SHAPES: Record<EmblemArchetype, Shape> = {
  // Offensive / raw damage — an upward blade/arrowhead: a hard, edged triangle.
  offensive: {
    verts: [
      [0, -1],
      [SQRT3_2, 0.7],
      [-SQRT3_2, 0.7],
    ],
    accent: "cross",
  },
  // Fire rate — a stacked chevron: the same blade, repeating.
  rate: { verts: chevron(), accent: "cross" },
  // Range — a wide lens: the silhouette is literally reach.
  range: { verts: lens(), accent: "orbit" },
  // Slow / chill — a six-point frost star.
  chill: { verts: starPoly(6, 0.38), accent: "star" },
  // Pierce / targeting — a narrow kite that reads as a needle.
  pierce: { verts: kite(), accent: "cross" },
  // Economy — the credit diamond.
  economy: { verts: regularPoly(4, -Math.PI / 2), accent: "ring" },
  // Synergy — a trefoil: three things touching.
  synergy: { verts: starPoly(3, 0.55), accent: "orbit" },
  // Trade-off — an hourglass: something is given up for something else.
  tradeoff: { verts: bowtie(), accent: "ring" },
  // Instant — a bolt: it happens once, now.
  instant: { verts: bolt(), accent: "star" },
  // Enemies — the blade inverted: it acts on THEM, not on you.
  enemies: {
    verts: [
      [0, 1],
      [-SQRT3_2, -0.7],
      [SQRT3_2, -0.7],
    ],
    accent: "cross",
  },
  // Defensive — a solid shield hex with an inner ring.
  defensive: { verts: regularPoly(6, -Math.PI / 2), accent: "ring" },
  // Utility — a precise orbital ring with an orbiting node.
  utility: { verts: regularPoly(24), accent: "orbit" },
  // Legendary (enriched-canvas fallback only) — a self-evident star sigil.
  legendary: { verts: starPoly(5, 0.45), accent: "star" },
};

export class SkillEmblem {
  /** The visible canvas (sits in the card's emblem host, behind the text). */
  readonly el: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D | null;
  private readonly shape: Shape;
  private readonly ramp: { core: string; mid: string; rim: string };
  private raf = 0;
  private hovered = false;
  private destroyed = false;
  private spin = 0;
  private orbit = 0;

  constructor(private readonly opts: SkillEmblemOptions) {
    this.shape = SHAPES[opts.archetype] ?? SHAPES.utility;
    this.ramp = deriveRamp(opts.colour);
    this.el = document.createElement("canvas");
    this.el.className = "skillcard-emblem-canvas";
    this.el.setAttribute("aria-hidden", "true");
    this.ctx = this.el.getContext("2d");
  }

  /** The ONE shared card-bloom (feature-detected; null headless / no-WebGL2). */
  static sharedBloom(): UIBloom | null {
    if (sharedBloom !== undefined) return sharedBloom;
    sharedBloom = null;
    try {
      if (typeof document === "undefined") return null;
      const probe = document.createElement("canvas").getContext("webgl2");
      if (!probe) return null;
      sharedBloom = new UIBloom();
    } catch {
      sharedBloom = null;
    }
    return sharedBloom;
  }

  /** Test/reset hook — drops the shared bloom context. */
  static resetSharedBloom(): void {
    sharedBloom?.destroy();
    sharedBloom = undefined;
  }

  /** Static dimensional draw — the resting state (idle ≈ 0). */
  drawStatic(): void {
    this.draw({ build: 1, boost: this.hovered ? 1.3 : 1, spin: 0 });
  }

  /** The §6.8 scan-construct: the emblem assembles from a scanline wipe + shards,
   *  spins up, then settles. Reduced-motion → an instant static draw. */
  playConstruct(delayMs = 0): void {
    if (this.destroyed) return;
    if (isReducedMotion()) {
      this.drawStatic();
      return;
    }
    const start = (): void => {
      if (this.destroyed) return;
      const t0 = now();
      const dur = 620;
      const tick = (): void => {
        if (this.destroyed) return;
        const t = Math.min(1, (now() - t0) / dur);
        const build = easeOut(t);
        // a punchy spin-in that decays to rest, plus a flare boost.
        const spin = (1 - build) * Math.PI * 1.4;
        const boost = 1 + (1 - t) * (0.6 + this.opts.bloomWeight);
        this.spin = spin;
        this.draw({ build, boost, spin });
        if (t < 1) this.raf = requestAnimationFrame(tick);
        else {
          this.spin = 0;
          this.rest();
        }
      };
      this.raf = requestAnimationFrame(tick);
    };
    if (delayMs > 0) setTimeout(start, delayMs);
    else start();
  }

  /** Hover power-up: spin, scale, brighten the core, raise an energy ring. */
  setHover(on: boolean): void {
    if (this.hovered === on) return;
    this.hovered = on;
    if (isReducedMotion()) return;
    if (on) this.runHoverLoop();
    else {
      // settle the spin back to a small idle value, then resume the gentle idle.
      const from = this.spin;
      const t0 = now();
      const tick = (): void => {
        if (this.destroyed || this.hovered) return;
        const t = Math.min(1, (now() - t0) / 240);
        this.spin = from * (1 - easeOut(t));
        this.draw({ build: 1, boost: 1 + (1 - t) * 0.3, spin: this.spin });
        if (t < 1) this.raf = requestAnimationFrame(tick);
        else this.rest();
      };
      if (this.raf) cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(tick);
    }
  }

  private runHoverLoop(): void {
    const t0 = now();
    const tick = (): void => {
      if (this.destroyed || !this.hovered) return;
      const dt = (now() - t0) / 1000;
      this.spin = dt * 1.6; // a confident continuous turn while hovered
      this.orbit = dt;
      this.draw({ build: 1, boost: 1.35, spin: this.spin, energyRing: true });
      this.raf = requestAnimationFrame(tick);
    };
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(tick);
  }

  /** Rest = the always-on gentle idle (owner CR 2026-06-05: "sie sollten sich
   *  immer bewegen … abgeschwächt wenn die Maus nicht drauf ist"). Reduced-motion
   *  → a single static draw (no loop). */
  private rest(): void {
    if (isReducedMotion()) {
      this.drawStatic();
      return;
    }
    this.runIdleLoop();
  }

  /** The damped cousin of the hover loop: a slow ± rock + a slow orbit drift + a
   *  faint breathe, so the emblem reads "alive / powered-on" even with no cursor
   *  on it. Throttled to ~30fps (a slow drift needs no more) to keep the cost low
   *  — and the draft is a between-wave modal, so the GPU budget is free here. */
  private runIdleLoop(): void {
    if (this.destroyed || this.hovered) return;
    const t0 = now();
    let last = -1000;
    const tick = (): void => {
      if (this.destroyed || this.hovered) return;
      const tnow = now();
      if (tnow - last >= 33) {
        last = tnow;
        const dt = (tnow - t0) / 1000;
        // ~±0.10 rad rock (a fraction of the hover's full turn) + slow orbit +
        // a 5% breathe — clearly weaker than hover, but never frozen.
        this.spin = Math.sin(dt * 0.6) * 0.1;
        this.orbit = dt * 0.4;
        const boost = 1 + 0.05 * Math.sin(dt * 1.1);
        this.draw({ build: 1, boost, spin: this.spin });
      }
      this.raf = requestAnimationFrame(tick);
    };
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(tick);
  }

  /** Select confirm: a one-shot flare punch that decays back. */
  powerUp(): void {
    if (this.destroyed) return;
    if (isReducedMotion()) {
      this.draw({ build: 1, boost: 1.5, spin: 0 });
      return;
    }
    const t0 = now();
    const dur = 360;
    const tick = (): void => {
      if (this.destroyed) return;
      const t = Math.min(1, (now() - t0) / dur);
      const k = Math.sin(Math.min(1, t * 1.4) * Math.PI) * (1 - t);
      this.draw({
        build: 1,
        boost: 1 + (1 + this.opts.bloomWeight) * Math.max(0, k),
        spin: this.spin,
        energyRing: true,
      });
      if (t < 1) this.raf = requestAnimationFrame(tick);
      else this.rest();
    };
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(tick);
  }

  // ─── The draw ────────────────────────────────────────────────────────────

  private draw(p: {
    build: number;
    boost: number;
    spin: number;
    energyRing?: boolean;
  }): void {
    const ctx = this.ctx;
    if (!ctx || this.destroyed) return;
    const host = this.el.parentElement;
    const cw = host?.clientWidth ?? 0;
    const ch = host?.clientHeight ?? 0;
    if (cw <= 0 || ch <= 0) return;

    const dpr = Math.max(
      1,
      displayDpr(),
    );
    const W = cw + OVERSCAN * 2;
    const H = ch + OVERSCAN * 2;
    this.el.width = Math.round(W * dpr);
    this.el.height = Math.round(H * dpr);
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, W, H);

    const cx = W / 2;
    const cy = H / 2;
    // Cap the emblem's max drawn radius so it stays a contained hero (never
    // frame-fills / overlaps the card title), independent of the art-window size.
    const R =
      Math.min(Math.min(cw, ch) * 0.4, this.opts.maxRadius ?? MAX_EMBLEM_R) *
      (0.9 + 0.1 * p.boost);
    const ramp = this.ramp;
    const intensity = (this.opts.intensity ?? 1) * p.boost;

    ctx.save();
    ctx.translate(cx, cy);
    ctx.rotate(p.spin);

    // The §6.8 scan-construct clip: a vertical band grows from the centre while
    // building, so the emblem "assembles" rather than popping in.
    if (p.build < 1) {
      const half = (H * (0.15 + 0.85 * p.build)) / 2;
      ctx.beginPath();
      ctx.rect(-W, -half, W * 2, half * 2);
      ctx.clip();
      ctx.globalAlpha = p.build;
    }

    // 1 — the extrusion: stack a few dark offset copies → fake thickness/bevel.
    const depth = Math.max(2, R * 0.16);
    const steps = 4;
    for (let s = steps; s >= 1; s--) {
      const d = (depth * s) / steps;
      ctx.save();
      ctx.translate(d * 0.5, d);
      this.tracePoly(ctx, R);
      ctx.fillStyle = ramp.rim;
      ctx.globalAlpha = (p.build < 1 ? p.build : 1) * 0.9;
      ctx.fill();
      ctx.restore();
    }

    // 2 — the lit front face: the real engine `emissiveFill` (hot core top-left,
    // mid identity, dark rim) → reads as a lit, fake-normal solid.
    this.tracePoly(ctx, R);
    ctx.save();
    ctx.clip();
    ctx.fillStyle = emissiveFill(ctx, -R * 0.18, -R * 0.18, R * 1.15, ramp);
    ctx.fillRect(-R * 1.6, -R * 1.6, R * 3.2, R * 3.2);
    ctx.restore();

    // 2.5 — the internal CORE-GLOW (the powered mainframe core): an additive
    // radial pulse at the heart, clipped to the silhouette → the strike-core /
    // firewall core / data-conduit hub reads as alive, not a flat glyph.
    this.tracePoly(ctx, R);
    ctx.save();
    ctx.clip();
    ctx.globalCompositeOperation = "lighter";
    const coreR = R * (0.5 + 0.1 * Math.sin(this.orbit * 1.6));
    const cg = ctx.createRadialGradient(0, 0, 0, 0, 0, coreR);
    cg.addColorStop(0, ramp.core);
    cg.addColorStop(0.5, ramp.mid);
    cg.addColorStop(1, "transparent");
    ctx.globalAlpha = Math.min(0.7, 0.3 + intensity * 0.22);
    ctx.beginPath();
    ctx.arc(0, 0, coreR, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // 3 — the bright rim stroke (the hot lit edge).
    this.tracePoly(ctx, R);
    ctx.lineWidth = Math.max(1.2, R * 0.05);
    ctx.strokeStyle = ramp.core;
    ctx.globalAlpha = Math.min(1, 0.55 + intensity * 0.25);
    ctx.stroke();

    // 4 — the archetype accent (the "tell").
    this.drawAccent(ctx, R, ramp, intensity);

    // 5 — the hover energy ring.
    if (p.energyRing) {
      ctx.globalCompositeOperation = "lighter";
      ctx.beginPath();
      ctx.arc(
        0,
        0,
        R * (1.25 + 0.1 * Math.sin(this.orbit * 4)),
        0,
        Math.PI * 2,
      );
      ctx.strokeStyle = ramp.core;
      ctx.globalAlpha = 0.4;
      ctx.lineWidth = Math.max(1, R * 0.03);
      ctx.stroke();
    }

    // 6 — additive vertex glow seeds (the `glowCache` lever) → bloom fuel.
    if (this.opts.bloomWeight > 0) {
      ctx.globalCompositeOperation = "lighter";
      const gr = R * 0.4 * Math.min(1.6, 0.8 + intensity * 0.5);
      const ga = Math.min(0.8, 0.4 * intensity);
      for (const [vx, vy] of this.shape.verts) {
        const sx = vx * R;
        const sy = vy * R;
        const g = ctx.createRadialGradient(sx, sy, 0, sx, sy, gr);
        g.addColorStop(0, ramp.core);
        g.addColorStop(1, "transparent");
        ctx.fillStyle = g;
        ctx.globalAlpha = ga;
        ctx.beginPath();
        ctx.arc(sx, sy, gr, 0, Math.PI * 2);
        ctx.fill();
      }
    }

    // 6.5 — a travelling catch-light specular across the face (the "turn it in
    // the light" on the hero itself), clipped to the silhouette.
    this.tracePoly(ctx, R);
    ctx.save();
    ctx.clip();
    ctx.globalCompositeOperation = "lighter";
    const lp = (((this.orbit * 0.5) % 1) + 1) % 1;
    const lx = lp * (R * 3) - R * 1.5;
    const lg = ctx.createLinearGradient(lx - R * 0.5, -R, lx + R * 0.5, R);
    lg.addColorStop(0, "transparent");
    lg.addColorStop(0.5, ramp.core);
    lg.addColorStop(1, "transparent");
    ctx.fillStyle = lg;
    ctx.globalAlpha = 0.2 + intensity * 0.08;
    ctx.fillRect(-R * 1.6, -R * 1.6, R * 3.2, R * 3.2);
    ctx.restore();

    // 6.6 — orbiting DATA-MOTES: additive telemetry points circling the hero
    // (the "mainframe-world" read). Count + brightness scale with the rarity.
    const moteN = 3 + Math.round(this.opts.bloomWeight * 4);
    ctx.save();
    ctx.globalCompositeOperation = "lighter";
    for (let i = 0; i < moteN; i++) {
      const a = this.orbit * 1.3 + (i / moteN) * Math.PI * 2;
      const rr = R * (1.16 + 0.12 * Math.sin(this.orbit * 2 + i));
      const mx = Math.cos(a) * rr;
      const my = Math.sin(a) * rr;
      const mr = Math.max(1, R * 0.08);
      const mgr = ctx.createRadialGradient(mx, my, 0, mx, my, mr);
      mgr.addColorStop(0, ramp.core);
      mgr.addColorStop(1, "transparent");
      ctx.fillStyle = mgr;
      ctx.globalAlpha = 0.5;
      ctx.beginPath();
      ctx.arc(mx, my, mr, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();

    ctx.restore();

    // 7 — route the hot edges through the ONE shared WebGL bloom (no per-card
    // context). Common (bloomWeight 0) skips it → a clean flat-lit emblem.
    if (this.opts.bloomWeight > 0 && this.opts.bloom) {
      const bloom = this.opts.bloom;
      bloom.setDpr(dpr);
      const out = bloom.process(this.el);
      if (out) {
        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalCompositeOperation = "lighter";
        ctx.globalAlpha = Math.min(1, this.opts.bloomWeight * p.boost);
        ctx.drawImage(out, 0, 0, this.el.width, this.el.height);
        ctx.globalAlpha = 1;
        ctx.globalCompositeOperation = "source-over";
      }
    }
  }

  private tracePoly(ctx: CanvasRenderingContext2D, R: number): void {
    const v = this.shape.verts;
    ctx.beginPath();
    ctx.moveTo(v[0]![0] * R, v[0]![1] * R);
    for (let i = 1; i < v.length; i++) ctx.lineTo(v[i]![0] * R, v[i]![1] * R);
    ctx.closePath();
  }

  private drawAccent(
    ctx: CanvasRenderingContext2D,
    R: number,
    ramp: { core: string; mid: string; rim: string },
    intensity: number,
  ): void {
    ctx.save();
    ctx.strokeStyle = ramp.core;
    ctx.fillStyle = ramp.core;
    ctx.globalAlpha = Math.min(1, 0.5 + intensity * 0.2);
    ctx.lineWidth = Math.max(1, R * 0.04);
    const a = this.shape.accent;
    if (a === "cross") {
      // STRIKE-CORE / blade forge — an edged crosshair + a forward chevron.
      ctx.beginPath();
      ctx.moveTo(0, -R * 0.36);
      ctx.lineTo(0, R * 0.34);
      ctx.moveTo(-R * 0.32, R * 0.08);
      ctx.lineTo(R * 0.32, R * 0.08);
      ctx.stroke();
      ctx.globalAlpha *= 0.8;
      ctx.beginPath();
      ctx.moveTo(-R * 0.22, -R * 0.04);
      ctx.lineTo(0, -R * 0.28);
      ctx.lineTo(R * 0.22, -R * 0.04);
      ctx.stroke();
    } else if (a === "ring") {
      // SHIELD-LATTICE / firewall bastion — concentric hex rings + a core node.
      for (let k = 0; k < 3; k++) {
        ctx.globalAlpha = Math.min(1, (0.55 - k * 0.13) + intensity * 0.15);
        ctx.beginPath();
        const rr = R * (0.5 - k * 0.13);
        for (let i = 0; i <= 6; i++) {
          const an = -Math.PI / 2 + (i / 6) * Math.PI * 2;
          const px = Math.cos(an) * rr;
          const py = Math.sin(an) * rr;
          i === 0 ? ctx.moveTo(px, py) : ctx.lineTo(px, py);
        }
        ctx.stroke();
      }
      ctx.globalAlpha = Math.min(1, 0.5 + intensity * 0.2);
      ctx.beginPath();
      ctx.arc(0, 0, R * 0.12, 0, Math.PI * 2);
      ctx.fill();
    } else if (a === "orbit") {
      // SCANNING ARRAY / data-conduit — a precise ring, a sweeping arm + nodes.
      ctx.globalAlpha *= 0.5;
      ctx.beginPath();
      ctx.arc(0, 0, R * 0.44, 0, Math.PI * 2);
      ctx.stroke();
      // the scan arm
      const sa = this.orbit * 1.6;
      ctx.globalAlpha *= 0.9;
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.lineTo(Math.cos(sa) * R * 0.44, Math.sin(sa) * R * 0.44);
      ctx.stroke();
      // three orbiting nodes
      for (let i = 0; i < 3; i++) {
        const oa = this.orbit * 2.2 + (i / 3) * Math.PI * 2;
        ctx.globalAlpha = Math.min(1, 0.6 + intensity * 0.2);
        ctx.beginPath();
        ctx.arc(
          Math.cos(oa) * R * 0.44,
          Math.sin(oa) * R * 0.44,
          R * 0.08,
          0,
          Math.PI * 2,
        );
        ctx.fill();
      }
    } else if (a === "star") {
      // legendary sigil — an inner ring + a bright heart.
      ctx.globalAlpha *= 0.6;
      ctx.beginPath();
      ctx.arc(0, 0, R * 0.34, 0, Math.PI * 2);
      ctx.stroke();
      ctx.globalAlpha = Math.min(1, 0.8 + intensity * 0.2);
      ctx.beginPath();
      ctx.arc(0, 0, R * 0.15, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  destroy(): void {
    this.destroyed = true;
    if (this.raf) cancelAnimationFrame(this.raf);
    this.raf = 0;
    this.el.remove();
  }
}

function now(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

/** Ease-out-cubic (mirrors AnimationUtils.settle) for the local tweens. */
function easeOut(t: number): number {
  const x = 1 - t;
  return 1 - x * x * x;
}
